import { db } from '@/core/db/dexie'
import type { DraftCarreteiroFicha } from '@/core/db/entities'
import { migrarEstado, type CarreteiroFichaState } from './carreteiroStore'

/**
 * Repositório de DRAFTS de fichas de carreteiro.
 *
 * Suporta múltiplas fichas em paralelo: operador pode ter N fichas em
 * andamento (caminhões diferentes, materiais diferentes na mesma viagem)
 * e alternar entre elas. Cada draft = uma sessão de viagens em construção.
 *
 * Persistido em Dexie pra sobreviver reload do PWA + funcionar offline.
 * Quando a ficha é finalmente salva (push pro outbox), o draft é deletado.
 *
 * IMPORTANTE: `viagensCount` e os campos top-level (obra, mve, dt etc) são
 * pré-computados pra que a listagem na index não precise desserializar
 * o JSON inteiro. O `state` carrega a verdade.
 */

/** Salva ou atualiza um draft. Idempotente — usa `state.id` como PK. */
export async function upsertDraft(state: CarreteiroFichaState): Promise<void> {
  // Defesa: se ainda não tem obra/MVE definidos, não persiste (lixo).
  if (state.obraId == null && state.mveId == null && state.viagens.length === 0) {
    return
  }
  const draft: DraftCarreteiroFicha = {
    id: state.id,
    obraId: state.obraId,
    obraNome: state.obraNome,
    mveId: state.mveId,
    mvePrefixo: state.mvePrefixo,
    mvePlaca: state.mvePlaca,
    dt: state.dt,
    tipo: state.tipo,
    viagensCount: state.viagens.length,
    lastUpdatedAt: Date.now(),
    // Serialização: snapshots de CatServico/etc são guardados como objetos
    // — JSON.stringify lida com isso (as funções do store são descartadas).
    state: JSON.stringify(state),
  }
  await db.draft_carreteiro.put(draft)
}

/**
 * Carrega um draft pelo id (= ficha id). Rascunhos gravados antes do
 * alinhamento com o SIGA passam por `migrarEstado`. null se não existe ou
 * o JSON não parseia.
 */
export async function loadDraft(id: string): Promise<CarreteiroFichaState | null> {
  const row = await db.draft_carreteiro.get(id)
  if (!row) return null
  try {
    return migrarEstado(JSON.parse(row.state))
  } catch (e) {
    console.warn('[draftCarreteiroRepo] falha ao parsear draft', id, e)
    return null
  }
}

/**
 * Lista drafts ordenados por última atualização (mais recente primeiro).
 * Não desserializa `state` — só metadata pra mostrar na lista.
 */
export async function listDrafts(): Promise<DraftCarreteiroFicha[]> {
  const all = await db.draft_carreteiro.toArray()
  return all.sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt)
}

/** Remove um draft (chamado quando a ficha é salva no outbox). */
export async function deleteDraft(id: string): Promise<void> {
  await db.draft_carreteiro.delete(id)
}

/**
 * Inicializa o auto-save: assina o zustand store e persiste o estado no
 * Dexie a cada mudança (debounced). Idempotente — chamado uma vez no boot
 * via `bootstrapAutoSave()`. Roda só DEPOIS da hidratação inicial pra não
 * salvar o estado vazio inicial em cima de um draft existente.
 *
 * Decisão: debounce de 700ms — rápido o bastante pra capturar pausas
 * naturais, lento o bastante pra não saturar IndexedDB em digitação.
 */
let autoSaveBootstrapped = false
export function bootstrapAutoSave(useCarreteiroStore: {
  subscribe: (cb: () => void) => () => void
  getState: () => CarreteiroFichaState
}): void {
  if (autoSaveBootstrapped) return
  autoSaveBootstrapped = true

  let timer: ReturnType<typeof setTimeout> | null = null
  useCarreteiroStore.subscribe(() => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      const state = useCarreteiroStore.getState()
      upsertDraft(state).catch((e) =>
        console.warn('[draftCarreteiroRepo] auto-save falhou', e),
      )
    }, 700)
  })
}
